import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, RouterProvider, Navigate, useLocation, Outlet } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { AccessGateProvider, useAccessGate, shouldBypassGate } from "./contexts/AccessGateContext";
import { CookieConsent } from "./components/CookieConsent";
import { ComingSoon } from "./components/ComingSoon";
import { ScrollToTop } from "./components/ScrollToTop";
import { useCartSync } from "./hooks/useCartSync";
import ProtectedRoute from "./components/ProtectedRoute";
import Index from "./pages/Index";
import MyGifts from "./pages/MyGifts";
import ProductDetail from "./pages/ProductDetail";
import UnifiedLogin from "./pages/UnifiedLogin";
import ResetPassword from "./pages/ResetPassword";
import MerchantDashboard from "./pages/MerchantDashboard";
import MerchantOnboarding from "./pages/MerchantOnboarding";
import ProfessionalOnboarding from "./pages/ProfessionalOnboarding";
import MerchantWidgetGuide from "./pages/MerchantWidgetGuide";
import SplitPaymentStatus from "./pages/SplitPaymentStatus";
import AdminDashboard from "./pages/AdminDashboard";
import Pricing from "./pages/Pricing";
import AcceptInvite from "./pages/AcceptInvite";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import Contact from "./pages/Contact";
import RefundPolicy from "./pages/RefundPolicy";
import Demo from "./pages/Demo";
import NotFound from "./pages/NotFound";
import OAuthSuccess from "./pages/OAuthSuccess";
import GiftGallery from "./pages/GiftGallery";
import EventDiscussion from "./pages/EventDiscussion";
import PurchaseDiscussion from "./pages/PurchaseDiscussion";
import EventManagement from "./pages/EventManagement";
import JoinEvent from "./pages/JoinEvent";
import MyActivity from "./pages/MyActivity";
import FAQ from "./pages/FAQ";
import ProfessionalFAQ from "./pages/ProfessionalFAQ";
import ShakatahCheckout from "./pages/ShakatahCheckout";
import ShakatahSplitSetup from "./pages/ShakatahSplitSetup";

const queryClient = new QueryClient();

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
function AccessGatedLayout() {
  const { hasAccess, grantAccess } = useAccessGate();
  const location = useLocation();

  // Sync carts with Shopify when user returns from checkout
  useCartSync();

  // Check if current route should bypass the gate
  const bypassGate = shouldBypassGate(location.pathname);
  
  // Also check for OAuth success param to prevent flash
  const searchParams = new URLSearchParams(location.search);
  const isOAuthFlow = searchParams.get("oauth_success") === "true";

  // Show Coming Soon for public routes without access (unless OAuth flow or bypass route)
  if (!hasAccess && !bypassGate && !isOAuthFlow) {
    return <ComingSoon onAccessGranted={grantAccess} />;
  }

  return (
    <>
      <ScrollToTop />
      <Outlet />
      <CookieConsent />
    </>
  );
}

// Create the router with data router API (required for useBlocker)
const router = createBrowserRouter([
  {
    element: <AccessGatedLayout />,
    children: [
      { path: "/", element: <Index /> },
      { path: "/my-purchases", element: <MyGifts /> },
      { path: "/my-activity", element: <MyActivity /> },
      { path: "/gallery", element: <GiftGallery /> },
      { path: "/pricing", element: <Pricing /> },
      { path: "/product/:handle", element: <ProductDetail /> },
      { path: "/shakatah-checkout", element: <ShakatahCheckout /> },
      { path: "/shakatah-split", element: <ShakatahSplitSetup /> },
      { path: "/shakatah-dashboard", element: <Navigate to="/merchant-dashboard" replace /> },
      { path: "/merchant-login", element: <UnifiedLogin /> },
      { path: "/login", element: <UnifiedLogin /> },
      { path: "/reset-password", element: <ResetPassword /> },
      { path: "/customer-login", element: <Navigate to="/login?type=customer" replace /> },
      { path: "/merchant/widget-guide", element: <ProtectedRoute><MerchantWidgetGuide /></ProtectedRoute> },
      { 
        path: "/merchant/onboarding", 
        element: <ProtectedRoute><MerchantOnboarding /></ProtectedRoute>
      },
      { 
        path: "/merchant-onboarding", 
        element: <ProtectedRoute><MerchantOnboarding /></ProtectedRoute>
      },
      { 
        path: "/professional/onboarding", 
        element: <ProtectedRoute><ProfessionalOnboarding /></ProtectedRoute>
      },
      { 
        path: "/merchant-dashboard", 
        element: <ProtectedRoute><MerchantDashboard /></ProtectedRoute>
      },
      { 
        path: "/admin", 
        element: <ProtectedRoute><AdminDashboard /></ProtectedRoute>
      },
      { path: "/split/:requestId", element: <SplitPaymentStatus /> },
      { path: "/events/:reminderId", element: <EventDiscussion /> },
      { path: "/purchases/:id", element: <PurchaseDiscussion /> },
      { path: "/join/:type/:token", element: <JoinEvent /> },
      { path: "/event-management", element: <EventManagement /> },
      { path: "/accept-invite", element: <AcceptInvite /> },
      { path: "/privacy-policy", element: <PrivacyPolicy /> },
      { path: "/privacy", element: <Navigate to="/privacy-policy" replace /> },
      { path: "/terms-of-service", element: <TermsOfService /> },
      { path: "/terms", element: <Navigate to="/terms-of-service" replace /> },
      { path: "/contact", element: <Contact /> },
      { path: "/refund-policy", element: <RefundPolicy /> },
      { path: "/refund", element: <Navigate to="/refund-policy" replace /> },
      { path: "/faq", element: <FAQ /> },
      { path: "/professional-faq", element: <ProfessionalFAQ /> },
      { path: "/demo", element: <Demo /> },
      { path: "/oauth-success", element: <OAuthSuccess /> },
      { path: "*", element: <NotFound /> },
    ],
  },
]);

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AccessGateProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <RouterProvider router={router} />
          </TooltipProvider>
        </AccessGateProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
